import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import fontSize from '../../theme/foundations/fontSizes';

type Props = {
  selected?: boolean;
  locale?: string;
  size?: 'lg' | 'md' | 'sm';
  date?: Date | undefined;
  placeholder?: string;
};

const minWidthDate = {
  sm: '60px',
  md: '100px',
  lg: '120px',
};

const StyledDate = styled.div<{ size: 'sm' | 'md' | 'lg'; selected?: boolean }>`
    padding: ${({ size }) => (size === 'sm' ? '8px' : ' 8px 12px')};
    min-width: ${({ size }) => minWidthDate[size]};
    background: #FFFFFF;
    box-shadow: ${({ selected }) =>
      selected ? '0px 1px 8px rgba(0, 0, 0, 0.2)' : 'unset'};
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    cursor: pointer;

    p {
        white-space: nowrap;
        pointer-events: none;
        margin: 0;
        padding: 0;
        font-size: ${({ size }) => fontSize[size]};
    }
`;

const DateField: React.FC<Props> = ({
  date,
  size = 'lg',
  locale = 'en-US',
  selected,
  placeholder = '-',
}: Props) => {
  const dateString = useMemo(() => {
    const d = date;
    if (!d) return placeholder;

    if (locale === 'pt-BR') {
      const day: string = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
      }).format(date);
      const month: string = new Intl.DateTimeFormat(locale, {
        month: 'short',
      })
        .format(date)
        .replace('.', '');
      const year: string = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
      }).format(date);
      return `${day} ${month} ${year}`;
    }

    const formatOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: size === 'sm' ? undefined : 'numeric',
    };

    return Intl.DateTimeFormat(locale, formatOptions).format(d);
  }, [date, locale, placeholder, size]);

  return (
    <StyledDate size={size} selected={selected}>
      <p>{dateString}</p>
    </StyledDate>
  );
};

export default DateField;
